import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FIELD_MAP,
  OTHER_FIELD_MAP,
  getFormIdByCity,
  getLocalitiesByCity,
} from "../config/googleForm.config";
import { validateFieldById } from "../common/utils/validations";
import { CheckIcon, ChevronRightIcon } from "../common/icons/icons";
import { questionData } from "../data/questionData";
import { useAppContext } from "../common/utils/CustomHooks/useAppContext";

const questions = questionData.flatMap((section) =>
  section.questions.map((q) => ({ ...q, section: section.title })),
);

const FormPage = () => {
  const isPrefillEnabled = process.env.REACT_APP_ENABLE_PREFILL === "true";
  const navigate = useNavigate();
  const { appState } = useAppContext();
  const { selectedCity } = appState;

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion.id];
  const progress = ((currentStep + 1) / questions.length) * 100;

  const currentSection = currentQuestion.section;
  const currentSectionIndex = questionData.findIndex(
    (s) => s.title === currentSection,
  );
  const totalSections = questionData.length;

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
    setErrorMessage("");
  };

  const toggleCheckbox = (option: string) => {
    const existing = (answers[currentQuestion.id]?.split(",") || []).filter(
      (o) => o.trim() !== "",
    );
    const newValue = existing.includes(option)
      ? existing.filter((o) => o !== option)
      : [...existing, option];
    handleAnswer(newValue.join(","));
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  // 🔧 Prefill demo data for testing (no other code touched)
  const autofillDemo = () => {
    const cityLocalities = getLocalitiesByCity(selectedCity);
    setAnswers({
      1: "Demo User",
      2: "25",
      3: "Male",
      4: cityLocalities[0], // Use first locality from selected city
      5: "9876543210",
      6: "@demo_user",
      7: "Female",
      8: "22-26",
      9: "Weekend,Weekday",
      10: "Daytime,Nighttime",
      11: "Coffee Date ☕,Dinner Date 🍽️,Activity Date 🎳",
      12: "2000+",
      13: "Software Engineer",
      14: "Music, Travel, Reading",
      15: "4",
      16: "No",
      17: "Occasionally",
      18: "Love them",
      19: "Friendly, Honest, Fun",
      20: "Kindness",
      21: "Dishonesty",
      22: "Maybe",
      23: [
        "I understand this is a blind date",
        "I’m over 18 and legally allowed to date.",
        "I agree to pay ₹499 (female)/ ₹999 (male) to book the experience when matched. Express date charges extra.",
        "I’ll be respectful and open-minded on the date.",
        "I understand that refunds are not guaranteed.",
      ].join(","),
    });
  };

  const handleNext = async () => {
    const qid = currentQuestion.id;
    const value = answers[qid] || "";

    const validation = validateFieldById(qid, value, currentQuestion.options);

    if (!validation.valid) {
      setErrorMessage(validation.message!);
      return;
    }

    if (currentStep < questions.length - 1) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    setIsSubmitting(true);

    const tokenRes = await fetch("/api/google-form-tokens");
    const { fbzx, draftResponse } = await tokenRes.json();

    // Create FormData
    const formData = new FormData();

    /* ======================================================
   BUILD FORM DATA WITH FULL STEP-BY-STEP LOGGING
   ====================================================== */
    for (const question of questions) {
      const qid = question.id;
      const value = answers[qid];
      const field = FIELD_MAP[qid];
      const otherField = OTHER_FIELD_MAP[qid];

      if (!field || !value) {
        continue;
      }

      /* ======================================================
      CASE 1 — MULTI CHECKBOX
     ====================================================== */
      if (question.type === "checkbox") {
        const selectedValues = value.split(",");
        const officialOptions = question.options || [];
        const googleOptions: string[] = [];
        const otherOptions: string[] = [];

        // Separate known vs other
        selectedValues.forEach((item) => {
          if (officialOptions.includes(item)) {
            googleOptions.push(item);
          } else {
            otherOptions.push(item);
          }
        });

        // Append known ones
        googleOptions.forEach((val) => {
          formData.append(field, val);
        });

        // Handle Other
        if (otherOptions.length > 0 && otherField) {
          formData.append(field, "__other_option__");
          formData.append(otherField, otherOptions.join(", "));
        }
        continue;
      }

      /* ======================================================
      CASE 2 — SELECT / TEXT / TEXTAREA / NUMBER
     ====================================================== */
      const hasOther = question.options?.includes("Other");

      // Case: User typed custom "Other" value
      if (hasOther && !question.options?.includes(value) && otherField) {
        formData.append(field, "__other_option__");
        formData.append(otherField, value);
        continue;
      }
      // Case: Normal value
      formData.append(field, value);
    }

    formData.append("fbzx", fbzx);
    formData.append("draftResponse", draftResponse);

    // Force Google to accept all pages
    const totalPages = 5; // You have 5 sections
    formData.append(
      "pageHistory",
      Array.from({ length: totalPages }, (_, i) => i).join(","),
    );

    /* ======================================================
   SUBMIT TO GOOGLE FORM
   ====================================================== */
    const formId = getFormIdByCity(selectedCity);
    try {
      await fetch(`https://docs.google.com/forms/d/e/${formId}/formResponse`, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });

      setShowSuccess(true);
      setTimeout(() => navigate("/success"), 1500);
    } catch (err) {
      console.error("❌ ERROR: Google Form submission failed:", err);
      alert("Something went wrong. Please try again!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen pt-24 bg-gradient-to-b from-background via-background to-primary/5 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          className="mb-12 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link
            to="/"
            className="inline-block text-primary hover:text-primary/80 mb-4 font-semibold"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl sm:text-5xl font-light italic text-primary mb-2">
            Tell Us About You
          </h1>
          <p className="text-foreground/60">
            Step {currentStep + 1} of {questions.length}
          </p>
        </motion.div>

        {/* Section indicator */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm uppercase tracking-wide text-primary font-semibold">
            {currentSection} ({currentSectionIndex + 1}/{totalSections})
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div className="mb-8 bg-muted rounded-full h-2 overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </motion.div>

        {/* Question Card */}
        <motion.div
          className="bg-card rounded-lg shadow-lg p-8 mb-8 border border-border"
          key={currentQuestion.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-6">
            {currentQuestion.question}
          </h2>

          {/* Error */}
          {errorMessage && (
            <p className="text-red-600 text-sm mb-4">{errorMessage}</p>
          )}

          {/* Input Types */}
          {currentQuestion.type === "text" && (
            <input
              type="text"
              value={answers[currentQuestion.id] || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Your answer..."
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          )}

          {currentQuestion.type === "number" && (
            <input
              type="number"
              value={answers[currentQuestion.id] || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Enter number..."
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          )}

          {currentQuestion.type === "textarea" && (
            <textarea
              value={answers[currentQuestion.id] || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Your detailed answer..."
              rows={5}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          )}

          {/* Multi-select Checkboxes */}
          {currentQuestion.type === "checkbox" && (
            <div className="space-y-2">
              {/* Use dynamic localities for question ID 4, otherwise use default options */}
              {(currentQuestion.id === 4
                ? getLocalitiesByCity(selectedCity)
                : currentQuestion.options
              )?.map((option) => {
                const selected = (
                  answers[currentQuestion.id]?.split(",") || []
                ).filter((o) => o.trim() !== "");
                const isOther = option.toLowerCase().includes("other");
                const isChecked =
                  selected.includes(option) ||
                  (isOther &&
                    selected.length > 0 &&
                    !(
                      currentQuestion.id === 4
                        ? getLocalitiesByCity(selectedCity)
                        : currentQuestion.options
                    )?.some((o) => selected.includes(o)));
                return (
                  <div key={option}>
                    <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-primary/5 transition">
                      <input
                        type="checkbox"
                        checked={Boolean(isChecked)}
                        onChange={() => toggleCheckbox(option)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="ml-3 text-foreground">{option}</span>
                    </label>

                    {isOther && isChecked && (
                      <input
                        type="text"
                        value={
                          !(
                            currentQuestion.id === 4
                              ? getLocalitiesByCity(selectedCity)
                              : currentQuestion.options
                          )?.includes(answers[currentQuestion.id])
                            ? answers[currentQuestion.id] || ""
                            : ""
                        }
                        onChange={(e) => handleAnswer(e.target.value)}
                        placeholder="Please specify..."
                        className="mt-2 w-full px-4 py-2 border border-border rounded-lg text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Single-select Radio */}
          {currentQuestion.type === "select" && (
            <div className="space-y-2">
              {currentQuestion.options?.map((option) => {
                const isOther = option.toLowerCase().includes("other");
                const selected = answers[currentQuestion.id];
                const isOtherSelected =
                  selected &&
                  (selected === "Other" ||
                    !currentQuestion.options?.includes(selected));
                return (
                  <div key={option}>
                    <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-primary/5 transition">
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={option}
                        checked={Boolean(
                          selected === option || (isOther && isOtherSelected),
                        )}
                        onChange={(e) => handleAnswer(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="ml-3 text-foreground">{option}</span>
                    </label>

                    {isOther && isOtherSelected && (
                      <input
                        type="text"
                        value={selected === "Other" ? "" : selected}
                        onChange={(e) => handleAnswer(e.target.value)}
                        placeholder="Please specify..."
                        className="mt-2 w-full px-4 py-2 border border-border rounded-lg text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Scale (1–5) */}
          {currentQuestion.type === "scale" && (
            <div className="flex justify-between mt-2">
              {currentQuestion.options?.map((num) => (
                <button
                  key={num}
                  onClick={() => handleAnswer(num)}
                  className={`w-10 h-10 rounded-full border-2 transition ${
                    answers[currentQuestion.id] === num
                      ? "bg-primary text-white"
                      : "border-border hover:border-primary"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {isPrefillEnabled && (
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={autofillDemo}
              className="text-sm px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition"
            >
              ⚙️ Prefill Demo Data
            </button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-4">
          <button
            onClick={handleBack}
            disabled={currentStep === 0 || isSubmitting}
            className="flex-1 px-6 py-3 border-2 border-primary text-primary rounded-lg font-semibold hover:bg-primary/5 transition disabled:opacity-50"
          >
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!isAnswered || isSubmitting || Boolean(errorMessage)}
            className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold shadow-lg hover:shadow-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting
              ? "Submitting..."
              : currentStep === questions.length - 1
                ? "Submit"
                : "Next"}
            {!showSuccess && !isSubmitting && <ChevronRightIcon />}
            {showSuccess && <CheckIcon />}
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <motion.div
            className="mt-6 text-center text-green-600 font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            ✓ Form submitted successfully!
          </motion.div>
        )}
      </div>
    </main>
  );
};

export { FormPage };
